import React, { useEffect, useState } from "react";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import { ButtonGroup } from "@mui/material";
import FontSelect from "./FontSelect";
import FontSizeSelect from "./FontSizeSelect";
import classNames from "classnames";
import ColorPicker from "./ColorPickerDialog";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

import { fonts } from "./fonts";

const DEFAULT_COLOR = "#000000";

const normalizeControlColor = (color) => {
  if (typeof color === "string" && color.trim()) {
    return color;
  }

  if (color?.hex) {
    return color.hex;
  }

  return DEFAULT_COLOR;
};

export const TopControls = ({
  toggleBold,
  toggleItalic,
  selectedObject,
  currentFont,
  currentFontSize,
  changeFont,
  changeFontSize,
  alignTextLeft,
  alignTextCenter,
  alignTextRight,
  bringSelectedForward,
  sendSelectedBackward,
  applyColor,
  color,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const selectedElement = selectedObject.element;
  const isColorableElement =
    selectedElement &&
    (selectedElement.type === "rect" ||
      selectedElement.type === "text" ||
      selectedElement.type === "i-text" ||
      selectedElement.type === "textbox");
  const pickerColor = normalizeControlColor(color);

  useEffect(() => {
    if (!isColorableElement) {
      setShowColorPicker(false);
    }
  }, [isColorableElement]);

  const isBold =
    selectedElement && selectedElement.fontWeight === "bold";
  const isItalic =
    selectedElement && selectedElement.fontStyle === "italic";
  const isAlignedLeft =
    selectedElement && selectedElement.textAlign === "left";
  const isAlignedRight =
    selectedElement && selectedElement.textAlign === "right";
  const isAlignedCenter =
    selectedElement && selectedElement.textAlign === "center";

  const adjustLineHeight = (increase) => {
    if (!selectedElement) return;

    const currentLineHeight = selectedElement.lineHeight || 1.16;
    const newLineHeight = increase
      ? Math.min(currentLineHeight + 0.1, 3) // max 3
      : Math.max(currentLineHeight - 0.1, 0.5); // min 0.5

    selectedElement.set('lineHeight', Number(newLineHeight.toFixed(2)));
    window.canvas.renderAll();
  };

  return (
    <div className="top-controls">
      <div>
        <button
          className="icon-button"
          onClick={() => setShowColorPicker(!showColorPicker)}
          disabled={!isColorableElement}
          title="Change Color"
        >
          <div
            style={{
              backgroundColor: pickerColor,
              width: "24px",
              height: "24px",
              border: "1px solid black",
              borderRadius: "50%",
            }}
          />
        </button>
        {showColorPicker && (
          <ColorPicker
            isOpen={showColorPicker}
            color={pickerColor}
            onColorSelect={(selectedColor) => {
              applyColor(selectedColor);
              setShowColorPicker(false);
            }}
            onColorHover={(selectedColor) => {
              applyColor(selectedColor);
            }}
            onClose={() => setShowColorPicker(false)}
          />
        )}
      </div>

      <ButtonGroup variant="outlined" size="small">
        <button
          className={classNames("icon-button", isBold && "icon-button-active")}
          onClick={toggleBold}
          disabled={
            !selectedObject.element ||
            (selectedObject.element.type !== "text" &&
              selectedObject.element.type !== "i-text" &&
              selectedObject.element.type !== "textbox")
          }
          title="Bold Text"
        >
          <FormatBoldIcon />
        </button>
        <button
          className={classNames("icon-button", isItalic && "icon-button-active")}
          onClick={toggleItalic}
          disabled={
            !selectedObject.element ||
            (selectedObject.element.type !== "text" &&
              selectedObject.element.type !== "i-text" &&
              selectedObject.element.type !== "textbox")
          }
          title="Italic Text"
        >
          <FormatItalicIcon />
        </button>
      </ButtonGroup>

      <ButtonGroup variant="outlined" size="small">
        <button
          className={classNames(
            "icon-button",
            isAlignedLeft && "icon-button-active"
          )}
          onClick={alignTextLeft}
          disabled={
            !selectedObject.element ||
            (selectedObject.element.type !== "text" &&
              selectedObject.element.type !== "i-text" &&
              selectedObject.element.type !== "textbox")
          }
          title="Align Left"
        >
          <FormatAlignLeftIcon />
        </button>
        <button
          className={classNames(
            "icon-button",
            isAlignedCenter && "icon-button-active"
          )}
          onClick={alignTextCenter}
          disabled={
            !selectedObject.element ||
            (selectedObject.element.type !== "text" &&
              selectedObject.element.type !== "i-text" &&
              selectedObject.element.type !== "textbox")
          }
          title="Align Center"
        >
          <FormatAlignCenterIcon />
        </button>
        <button
          className={classNames(
            "icon-button",
            isAlignedRight && "icon-button-active"
          )}
          onClick={alignTextRight}
          disabled={
            !selectedObject.element ||
            (selectedObject.element.type !== "text" &&
              selectedObject.element.type !== "i-text" &&
              selectedObject.element.type !== "textbox")
          }
          title="Align Right"
        >
          <FormatAlignRightIcon />
        </button>
      </ButtonGroup>

      <ButtonGroup variant="outlined" size="small">
        <button
          className={"icon-button"}
          onClick={bringSelectedForward}
          disabled={!selectedObject.element}
          title="Bring Forward"
        >
          <ArrowUpwardIcon />
        </button>
        <button
          className={"icon-button"}
          onClick={sendSelectedBackward}
          disabled={!selectedObject.element}
          title="Send Backward"
        >
          <ArrowDownwardIcon />
        </button>
      </ButtonGroup>

      {/* <ButtonGroup variant="outlined" size="small">
        <button
          className="icon-button"
          onClick={() => adjustLineHeight(true)}
          disabled={
            !selectedObject.element ||
            (selectedObject.element.type !== "text" &&
              selectedObject.element.type !== "i-text" &&
              selectedObject.element.type !== "textbox" ||
              selectedObject.element.field
            )
          }
          title="Increase Line Height"
        >
          <UnfoldMoreIcon />
        </button>
        <button
          className="icon-button"
          onClick={() => adjustLineHeight(false)}
          disabled={
            !selectedObject.element ||
            (selectedObject.element.type !== "text" &&
              selectedObject.element.type !== "i-text" &&
              selectedObject.element.type !== "textbox" ||
              selectedObject.element.field)
          }
          title="Decrease Line Height"
        >
          <UnfoldLessIcon />
        </button>
      </ButtonGroup> */}

      <FontSizeSelect
        selectedSize={currentFontSize}
        onFontSizeChange={changeFontSize}
        disabled={
          !selectedObject.element ||
          (selectedObject.element.type !== "text" &&
            selectedObject.element.type !== "i-text" &&
            selectedObject.element.type !== "textbox")
        }
      />
      <FontSelect
        fonts={fonts}
        selectedFont={currentFont}
        onFontChange={changeFont}
        disabled={
          !selectedObject.element ||
          (selectedObject.element.type !== "text" &&
            selectedObject.element.type !== "i-text" &&
            selectedObject.element.type !== "textbox")
        }
      />
    </div>
  );
};
